import {
  Badge,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { ReactComponent as BackIcon } from 'assets/icons/chevron_left.svg';
import { KeyboardEvent, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatContainer } from './ChatContainer';
import { DesignAIModalContentProps, DesignAIModalProps } from './DesignAI.types';
import { useDesignAIContext } from './DesignAIContext';

export const DesignAIModal = ({ isOpen }: DesignAIModalProps) => {
  const { redirectToTemplates } = useDesignAIContext();

  return (
    <Modal isOpen={isOpen} onClose={redirectToTemplates} isCentered size='4xl' trapFocus={false}>
      <ModalOverlay />
      <DesignAIModalContent />
    </Modal>
  );
};

const DesignAIModalContent = memo((inProps: DesignAIModalContentProps) => {
  const { redirectToTemplates, firstFocusableElementRef, lastFocusableElementRef } = useDesignAIContext();
  const { t } = useTranslation('design_generator', { keyPrefix: 'designAI' });
  const ref = useRef<HTMLDivElement>(null);

  const headingLabel = t('heading');
  const betaLabel = t('beta');
  const ariaHeader = `${headingLabel} ${betaLabel}`;

  // Focus on the content when the modal is opened, [INFO-153.1]
  useEffect(() => {
    ref.current?.focus();
  }, []);

  /**
   * [INFO-153.3] Focus in the modal should be cyclic, (from the last element to the first and from the first element to the last
   * [INFO-153.4] The modal should be closed by pressing the ESC key.
   * @param e
   */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      redirectToTemplates();
    }

    if (e.key === 'Tab') {
      if (!e.shiftKey && e.target === lastFocusableElementRef.current) {
        e.preventDefault();
        e.stopPropagation();
        firstFocusableElementRef.current?.focus();
      }

      if (e.shiftKey && e.target === firstFocusableElementRef.current) {
        e.preventDefault();
        e.stopPropagation();
        lastFocusableElementRef.current?.focus();
      }
    }
  };

  return (
    <ModalContent
      onKeyDown={onKeyDown}
      ref={ref}
      height='xl'
      maxH='100%'
      m='0'
      width={{ base: '92%', md: 'full' }}
      {...inProps}
    >
      <ModalHeader p='6' aria-label={ariaHeader}>
        <Flex alignItems={'center'}>
          <Button
            ref={firstFocusableElementRef}
            display={{ base: 'none', md: 'block' }}
            onClick={redirectToTemplates}
            leftIcon={<BackIcon style={{ marginRight: '0.5rem' }} />}
            size='xs'
            variant='ghost'
            position='absolute'
          >
            {t('backButton')}
          </Button>
          <Heading
            display='flex'
            alignItems='center'
            justifyContent='center'
            width='full'
            textAlign='center'
            fontSize='xl'
          >
            {headingLabel}
            <Badge variant='solid' colorScheme='green' ml='2' lineHeight='base' fontWeight='medium'>
              {betaLabel}
            </Badge>
          </Heading>
        </Flex>
        <ModalCloseButton m='2.5' />
      </ModalHeader>
      <ModalBody as={ChatContainer} pl='6' pb='6' pr='2' overflow='hidden' />
    </ModalContent>
  );
});

DesignAIModalContent.displayName = 'DesignAIModalContent';
